// src/screens/PasswordManagerScreen.jsx
//
// Zero-knowledge password vault, styled to match abc-dashboard's existing
// screens (inline styles, same dark theme as App.jsx / AppRegistryScreen.jsx).
//
// Unlock works two ways: master password, or a one-time recovery code shown
// at setup. Both independently decrypt the same underlying vault key — see
// vaultCrypto.js for how.

import { useState, useEffect, useRef } from "react";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  generateSalt,
  generateVaultKeyMaterial,
  generateRecoveryCode,
  deriveKey,
  importVaultKey,
  encryptField,
  decryptField,
  createCanary,
  verifyCanary,
} from "../vaultCrypto";
import {
  entriesToBitwardenJson,
  parseBitwardenJson,
  splitNewAndDuplicate,
  downloadTextFile,
} from "../vaultTransfer";

// Client-side gate only — real enforcement is in firestore.rules.
const AUTHORIZED_EMAILS = ["layakarudu@gmail.com"];

const VAULT_META_DOC = doc(db, "vaultMeta", "config");
const VAULT_ENTRIES_COLLECTION = "vaultEntries";

// Entry types this vault understands. Each controls the labels shown in the
// form/table and whether the secret is a single-line value or a multiline
// blob (e.g. a Firebase service account JSON key).
const ENTRY_TYPES = {
  login: {
    label: "Login",
    icon: "🔑",
    usernameLabel: "Username / Email",
    secretLabel: "Password",
    secretMultiline: false,
    urlLabel: "URL (optional)",
  },
  github_pat: {
    label: "GitHub PAT",
    icon: "🐙",
    usernameLabel: "Account / Org (optional)",
    secretLabel: "Personal Access Token",
    secretMultiline: true,
    urlLabel: "Repo URL (optional)",
  },
  api_key: {
    label: "API Key / Secret",
    icon: "🗝️",
    usernameLabel: "Key Name / Client ID (optional)",
    secretLabel: "API Key / Secret Value",
    secretMultiline: true,
    urlLabel: "Service URL (optional)",
  },
  firebase_service_account: {
    label: "Firebase Service Account",
    icon: "🔥",
    usernameLabel: "Project ID (optional)",
    secretLabel: "Service Account JSON",
    secretMultiline: true,
    urlLabel: "Console URL (optional)",
  },
};
const DEFAULT_ENTRY_TYPE = "login";

const colors = {
  bg: "#111217",
  panel: "#1b1d27",
  panelAlt: "#202332",
  input: "#303446",
  border: "#333",
  accent: "#6c63ff",
  text: "#fff",
  muted: "#aab2c5",
  danger: "#e5484d",
  warn: "#f0c040",
};

const styles = {
  page: { padding: 24, color: colors.text, fontFamily: "'Segoe UI', Arial, sans-serif" },
  centerCard: {
    maxWidth: 460,
    margin: "60px auto",
    background: colors.panel,
    borderRadius: 12,
    padding: 28,
    border: `1px solid ${colors.border}`,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    marginBottom: 12,
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    background: colors.input,
    color: colors.text,
    fontSize: 14,
    boxSizing: "border-box",
  },
  button: { padding: "10px 16px", border: "none", borderRadius: 8, background: colors.accent, color: "#fff", cursor: "pointer", fontSize: 14 },
  buttonSecondary: { padding: "10px 16px", border: "none", borderRadius: 8, background: colors.input, color: colors.text, cursor: "pointer", fontSize: 14 },
  buttonDanger: { padding: "6px 10px", border: "none", borderRadius: 6, background: "transparent", color: colors.danger, cursor: "pointer", fontSize: 12.5 },
  linkButton: { background: "none", border: "none", color: colors.accent, cursor: "pointer", fontSize: 12.5, padding: 0, marginLeft: 8 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "10px 12px", borderBottom: `1px solid ${colors.border}`, color: colors.muted, fontWeight: 600 },
  td: { padding: "10px 12px", borderBottom: `1px solid ${colors.border}` },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  recoveryBox: {
    background: colors.panelAlt,
    border: `1px solid ${colors.warn}`,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    fontFamily: "monospace",
    fontSize: 16,
    textAlign: "center",
    letterSpacing: 1,
  },
};

// Steps for first-time setup: choose-password -> show-recovery-code -> done
export default function PasswordManagerScreen() {
  const { user } = useAuth();
  const isAuthorized = AUTHORIZED_EMAILS.includes((user?.email || "").toLowerCase());

  const [loading, setLoading] = useState(true);
  const [vaultExists, setVaultExists] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [vaultKey, setVaultKey] = useState(null); // the CryptoKey used for entries

  // setup wizard state
  const [setupStep, setSetupStep] = useState("choose-password");
  const [newMasterPassword, setNewMasterPassword] = useState("");
  const [confirmMasterPassword, setConfirmMasterPassword] = useState("");
  const [generatedRecoveryCode, setGeneratedRecoveryCode] = useState("");
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const [pendingVaultMeta, setPendingVaultMeta] = useState(null);
  const [setupError, setSetupError] = useState("");

  // unlock state
  const [unlockMode, setUnlockMode] = useState("password"); // "password" | "recovery"
  const [unlockInput, setUnlockInput] = useState("");
  const [unlockError, setUnlockError] = useState("");

  const [entries, setEntries] = useState([]);
  const [revealedIds, setRevealedIds] = useState(new Set());
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [transferMsg, setTransferMsg] = useState("");
  const importInputRef = useRef(null);

  useEffect(() => {
    if (!isAuthorized) {
      setLoading(false);
      return;
    }
    getDoc(VAULT_META_DOC).then((snap) => {
      setVaultExists(snap.exists());
      setLoading(false);
    });
  }, [isAuthorized]);

  // --- Setup: step 1, choose master password, generate everything ---
  async function handleChoosePassword(e) {
    e.preventDefault();
    setSetupError("");

    if (newMasterPassword.length < 12) {
      setSetupError("Master password should be at least 12 characters.");
      return;
    }
    if (newMasterPassword !== confirmMasterPassword) {
      setSetupError("Passwords don't match.");
      return;
    }

    const saltPassword = generateSalt();
    const saltRecovery = generateSalt();
    const vaultKeyMaterial = generateVaultKeyMaterial();
    const recoveryCode = generateRecoveryCode();

    const keyFromPassword = await deriveKey(newMasterPassword, saltPassword);
    const keyFromRecovery = await deriveKey(recoveryCode, saltRecovery);

    const encByPassword = await encryptField(keyFromPassword, vaultKeyMaterial);
    const encByRecovery = await encryptField(keyFromRecovery, vaultKeyMaterial);

    const importedVaultKey = await importVaultKey(vaultKeyMaterial);
    const canary = await createCanary(importedVaultKey);

    setPendingVaultMeta({
      saltPassword,
      saltRecovery,
      encryptedVaultKeyByPassword: encByPassword.ciphertext,
      passwordIv: encByPassword.iv,
      encryptedVaultKeyByRecovery: encByRecovery.ciphertext,
      recoveryIv: encByRecovery.iv,
      canaryCiphertext: canary.ciphertext,
      canaryIv: canary.iv,
      createdOn: serverTimestamp(),
      createdBy: user?.email || null,
    });
    setGeneratedRecoveryCode(recoveryCode);
    setVaultKey(importedVaultKey);
    setSetupStep("show-recovery-code");
  }

  // --- Setup: step 2, confirm recovery code saved, write to Firestore ---
  async function handleFinishSetup() {
    if (!recoveryConfirmed) return;
    await setDoc(VAULT_META_DOC, pendingVaultMeta);
    setVaultExists(true);
    setUnlocked(true);
    setNewMasterPassword("");
    setConfirmMasterPassword("");
    setPendingVaultMeta(null);
  }

  // --- Unlock with either master password or recovery code ---
  async function handleUnlock(e) {
    e.preventDefault();
    setUnlockError("");

    const metaSnap = await getDoc(VAULT_META_DOC);
    if (!metaSnap.exists()) {
      setUnlockError("Vault metadata not found.");
      return;
    }
    const meta = metaSnap.data();

    try {
      const salt = unlockMode === "password" ? meta.saltPassword : meta.saltRecovery;
      const encryptedVaultKey =
        unlockMode === "password" ? meta.encryptedVaultKeyByPassword : meta.encryptedVaultKeyByRecovery;
      const iv = unlockMode === "password" ? meta.passwordIv : meta.recoveryIv;

      const derivedFromInput = await deriveKey(unlockInput, salt);
      const vaultKeyMaterial = await decryptField(derivedFromInput, encryptedVaultKey, iv);
      const importedVaultKey = await importVaultKey(vaultKeyMaterial);

      const isCorrect = await verifyCanary(importedVaultKey, meta.canaryCiphertext, meta.canaryIv);
      if (!isCorrect) {
        setUnlockError(unlockMode === "password" ? "Incorrect master password." : "Incorrect recovery code.");
        return;
      }

      setVaultKey(importedVaultKey);
      setUnlocked(true);
      setUnlockInput("");
    } catch {
      setUnlockError(unlockMode === "password" ? "Incorrect master password." : "Incorrect recovery code.");
    }
  }

  function handleLock() {
    setVaultKey(null);
    setUnlocked(false);
    setEntries([]);
    setRevealedIds(new Set());
  }

  useEffect(() => {
    if (!unlocked || !vaultKey) return;

    const unsubscribe = onSnapshot(collection(db, VAULT_ENTRIES_COLLECTION), async (snapshot) => {
      const decrypted = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          let password = "[unable to decrypt]";
          let notes = "";
          try {
            password = await decryptField(vaultKey, data.encryptedPassword, data.passwordIv);
            if (data.encryptedNotes) notes = await decryptField(vaultKey, data.encryptedNotes, data.notesIv);
          } catch {
            // corrupted entry or key mismatch — leave placeholder
          }
          return {
            id: docSnap.id,
            type: data.type || DEFAULT_ENTRY_TYPE,
            title: data.title,
            username: data.username,
            url: data.url || "",
            password,
            notes,
          };
        })
      );
      setEntries(decrypted);
    });

    return () => unsubscribe();
  }, [unlocked, vaultKey]);

  async function handleSaveEntry(formData) {
    const { type, title, username, url, password, notes } = formData;
    const encPassword = await encryptField(vaultKey, password);
    const encNotes = notes ? await encryptField(vaultKey, notes) : null;

    const payload = {
      type: type || DEFAULT_ENTRY_TYPE,
      title,
      username,
      url,
      encryptedPassword: encPassword.ciphertext,
      passwordIv: encPassword.iv,
      encryptedNotes: encNotes ? encNotes.ciphertext : "",
      notesIv: encNotes ? encNotes.iv : "",
      updatedOn: serverTimestamp(),
    };

    if (editingEntry) {
      await updateDoc(doc(db, VAULT_ENTRIES_COLLECTION, editingEntry.id), payload);
    } else {
      await addDoc(collection(db, VAULT_ENTRIES_COLLECTION), { ...payload, createdOn: serverTimestamp() });
    }
    setEditingEntry(null);
    setShowAddForm(false);
  }

  // --- Export / import (Bitwarden JSON format, see vaultTransfer.js) ---
  function handleExport() {
    const readable = entries.filter((e) => e.password !== "[unable to decrypt]");
    const unreadable = entries.length - readable.length;
    if (readable.length === 0) {
      setTransferMsg("Nothing to export.");
      return;
    }
    if (
      !window.confirm(
        "This downloads " + readable.length + " entries as a PLAIN, UNENCRYPTED file.\n\n" +
          "Import it into Bitwarden, then delete the file (and empty the recycle bin). Continue?"
      )
    ) return;
    const data = entriesToBitwardenJson(readable);
    downloadTextFile("crm-vault-export-" + new Date().toISOString().slice(0, 10) + ".json", JSON.stringify(data, null, 2));
    setTransferMsg(
      "Exported " + readable.length + " entries" + (unreadable ? " (" + unreadable + " could not be decrypted and were left out)" : "") +
        ". Delete the downloaded file after importing it."
    );
  }

  async function addEntry(formData) {
    const { type, title, username, url, password, notes } = formData;
    const encPassword = await encryptField(vaultKey, password);
    const encNotes = notes ? await encryptField(vaultKey, notes) : null;
    await addDoc(collection(db, VAULT_ENTRIES_COLLECTION), {
      type: type || DEFAULT_ENTRY_TYPE,
      title,
      username: username || "",
      url: url || "",
      encryptedPassword: encPassword.ciphertext,
      passwordIv: encPassword.iv,
      encryptedNotes: encNotes ? encNotes.ciphertext : "",
      notesIv: encNotes ? encNotes.iv : "",
      updatedOn: serverTimestamp(),
      createdOn: serverTimestamp(),
    });
  }

  async function handleImportFile(ev) {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = "";
    if (!file) return;
    setTransferMsg("");
    const text = await file.text();
    const { entries: parsed, skipped, error } = parseBitwardenJson(text);
    if (error) {
      setTransferMsg(error);
      return;
    }
    const { toAdd, duplicates } = splitNewAndDuplicate(parsed, entries);
    const summary = duplicates.length + " already in the vault, " + skipped.length + " skipped";
    if (toAdd.length === 0) {
      setTransferMsg("Nothing new to import (" + summary + ").");
      return;
    }
    if (!window.confirm("Import " + toAdd.length + " new entries? (" + summary + ")\n\nDelete the file afterwards.")) return;
    let done = 0;
    try {
      for (const e of toAdd) {
        await addEntry(e);
        done++;
      }
      setTransferMsg("Imported " + done + " entries (" + summary + "). Delete the imported file now.");
    } catch (err) {
      setTransferMsg("Import stopped after " + done + " of " + toAdd.length + " entries: " + err.message);
    }
  }

  async function handleDeleteEntry(id) {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    await deleteDoc(doc(db, VAULT_ENTRIES_COLLECTION, id));
  }

  function toggleReveal(id) {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function copyToClipboard(text) {
    await navigator.clipboard.writeText(text);
  }

  if (!isAuthorized) {
    return (
      <div style={styles.page}>
        <p style={{ color: colors.muted }}>You don't have access to this screen.</p>
      </div>
    );
  }

  if (loading) return <div style={styles.page}>Loading vault…</div>;

  // --- First-time setup wizard ---
  if (!vaultExists && !unlocked) {
    if (setupStep === "show-recovery-code") {
      return (
        <div style={styles.page}>
          <div style={styles.centerCard}>
            <h2 style={{ marginTop: 0 }}>Save Your Recovery Code</h2>
            <p style={{ color: colors.muted, fontSize: 13 }}>
              If you ever forget your master password, this code is the <b>only</b> other way in. Write it down and
              store it somewhere safe — a locked drawer, a safe, or a printed copy. Do not save it as a screenshot
              on the same device.
            </p>
            <div style={styles.recoveryBox}>{generatedRecoveryCode}</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: colors.muted, marginBottom: 16 }}>
              <input type="checkbox" checked={recoveryConfirmed} onChange={(e) => setRecoveryConfirmed(e.target.checked)} />
              I've saved this recovery code somewhere safe
            </label>
            <button
              style={{ ...styles.button, width: "100%", opacity: recoveryConfirmed ? 1 : 0.5 }}
              disabled={!recoveryConfirmed}
              onClick={handleFinishSetup}
            >
              Continue to Vault
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={styles.page}>
        <div style={styles.centerCard}>
          <h2 style={{ marginTop: 0 }}>Set Up Password Vault</h2>
          <p style={{ color: colors.muted, fontSize: 13 }}>
            Choose a master password. You'll also get a one-time recovery code on the next screen as a backup.
          </p>
          <form onSubmit={handleChoosePassword}>
            <input
              type="password"
              placeholder="Master password"
              value={newMasterPassword}
              onChange={(e) => setNewMasterPassword(e.target.value)}
              style={styles.input}
              autoFocus
            />
            <input
              type="password"
              placeholder="Confirm master password"
              value={confirmMasterPassword}
              onChange={(e) => setConfirmMasterPassword(e.target.value)}
              style={styles.input}
            />
            {setupError && <p style={{ color: colors.danger, fontSize: 13 }}>{setupError}</p>}
            <button type="submit" style={{ ...styles.button, width: "100%" }}>
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- Unlock screen ---
  if (!unlocked) {
    return (
      <div style={styles.page}>
        <div style={styles.centerCard}>
          <h2 style={{ marginTop: 0 }}>Unlock Password Vault</h2>
          <p style={{ color: colors.muted, fontSize: 13 }}>
            {unlockMode === "password"
              ? "Enter your master password. It's never sent or stored anywhere."
              : "Enter your recovery code (shown once at setup)."}
          </p>
          <form onSubmit={handleUnlock}>
            <input
              type={unlockMode === "password" ? "password" : "text"}
              placeholder={unlockMode === "password" ? "Master password" : "Recovery code"}
              value={unlockInput}
              onChange={(e) => setUnlockInput(e.target.value)}
              style={{ ...styles.input, fontFamily: unlockMode === "recovery" ? "monospace" : "inherit" }}
              autoFocus
            />
            {unlockError && <p style={{ color: colors.danger, fontSize: 13 }}>{unlockError}</p>}
            <button type="submit" style={{ ...styles.button, width: "100%", marginBottom: 8 }}>
              Unlock
            </button>
          </form>
          <button
            style={styles.linkButton}
            onClick={() => {
              setUnlockMode(unlockMode === "password" ? "recovery" : "password");
              setUnlockInput("");
              setUnlockError("");
            }}
          >
            {unlockMode === "password" ? "Forgot your master password? Use recovery code" : "Use master password instead"}
          </button>
        </div>
      </div>
    );
  }

  // --- Unlocked: vault view ---
  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h2 style={{ margin: 0 }}>🔒 Password Manager</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.button} onClick={() => { setEditingEntry(null); setShowAddForm(true); }}>
            + Add Entry
          </button>
          <button style={styles.buttonSecondary} onClick={() => importInputRef.current && importInputRef.current.click()}>
            Import
          </button>
          <input ref={importInputRef} type="file" accept=".json,application/json" style={{ display: "none" }} onChange={handleImportFile} />
          <button style={styles.buttonSecondary} onClick={handleExport}>
            Export
          </button>
          <button style={styles.buttonSecondary} onClick={handleLock}>
            Lock Vault
          </button>
        </div>
      </div>

      {transferMsg && <p style={{ color: colors.warn, fontSize: 13, marginTop: 0 }}>{transferMsg}</p>}

      {showAddForm && (
        <EntryForm initial={editingEntry} onCancel={() => { setShowAddForm(false); setEditingEntry(null); }} onSave={handleSaveEntry} />
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Username / Label</th>
            <th style={styles.th}>Secret</th>
            <th style={styles.th}>URL</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const typeConfig = ENTRY_TYPES[entry.type] || ENTRY_TYPES[DEFAULT_ENTRY_TYPE];
            const revealed = revealedIds.has(entry.id);
            return (
              <tr key={entry.id}>
                <td style={styles.td}>
                  <span title={typeConfig.label}>{typeConfig.icon} {typeConfig.label}</span>
                </td>
                <td style={styles.td}>{entry.title}</td>
                <td style={styles.td}>{entry.username}</td>
                <td style={{ ...styles.td, fontFamily: "monospace" }}>
                  {revealed ? (
                    typeConfig.secretMultiline ? (
                      <pre
                        style={{
                          margin: 0,
                          maxWidth: 320,
                          maxHeight: 120,
                          overflow: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 6,
                          padding: 8,
                          fontSize: 12,
                        }}
                      >
                        {entry.password}
                      </pre>
                    ) : (
                      entry.password
                    )
                  ) : (
                    "••••••••"
                  )}
                  <br />
                  <button style={styles.linkButton} onClick={() => toggleReveal(entry.id)}>
                    {revealed ? "Hide" : "Reveal"}
                  </button>
                  <button style={styles.linkButton} onClick={() => copyToClipboard(entry.password)}>
                    Copy
                  </button>
                </td>
                <td style={styles.td}>
                  {entry.url && (
                    <a href={entry.url} target="_blank" rel="noreferrer" style={{ color: colors.accent }}>
                      {entry.url}
                    </a>
                  )}
                </td>
                <td style={styles.td}>
                  <button style={styles.linkButton} onClick={() => { setEditingEntry(entry); setShowAddForm(true); }}>
                    Edit
                  </button>
                  <button style={styles.buttonDanger} onClick={() => handleDeleteEntry(entry.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {entries.length === 0 && <p style={{ color: colors.muted, marginTop: 16 }}>No entries yet.</p>}
    </div>
  );
}

function EntryForm({ initial, onSave, onCancel }) {
  const [type, setType] = useState(initial?.type || DEFAULT_ENTRY_TYPE);
  const [title, setTitle] = useState(initial?.title || "");
  const [username, setUsername] = useState(initial?.username || "");
  const [url, setUrl] = useState(initial?.url || "");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState(initial?.notes || "");

  const typeConfig = ENTRY_TYPES[type] || ENTRY_TYPES[DEFAULT_ENTRY_TYPE];

  function handleSubmit(e) {
    e.preventDefault();
    onSave({ type, title, username, url, password, notes });
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginBottom: 20, padding: 16, borderRadius: 10, background: colors.panelAlt, border: `1px solid ${colors.border}` }}>
      <select value={type} onChange={(e) => setType(e.target.value)} style={styles.input}>
        {Object.entries(ENTRY_TYPES).map(([key, cfg]) => (
          <option key={key} value={key}>
            {cfg.icon} {cfg.label}
          </option>
        ))}
      </select>
      <input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} style={styles.input} required />
      <input placeholder={typeConfig.usernameLabel} value={username} onChange={(e) => setUsername(e.target.value)} style={styles.input} />
      {typeConfig.secretMultiline ? (
        <textarea
          placeholder={initial ? `${typeConfig.secretLabel} (re-enter to confirm/change)` : typeConfig.secretLabel}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ ...styles.input, minHeight: 100, fontFamily: "monospace", fontSize: 12.5 }}
          required
        />
      ) : (
        <input
          type="password"
          placeholder={initial ? `${typeConfig.secretLabel} (re-enter to confirm/change)` : typeConfig.secretLabel}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.input}
          required
        />
      )}
      <input placeholder={typeConfig.urlLabel} value={url} onChange={(e) => setUrl(e.target.value)} style={styles.input} />
      <textarea
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        style={{ ...styles.input, minHeight: 60, fontFamily: "inherit" }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" style={styles.button}>Save</button>
        <button type="button" style={styles.buttonSecondary} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
